import { useEffect } from 'react';

import { api } from '@/lib/api/endpoints';

const HEARTBEAT_MS = 20_000;
const INTERVIEW_PATH =
  /^\/interviews\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

function currentPresencePayload(): { path: string; interview_id: string | null } {
  const path = window.location.pathname || '/';
  const match = INTERVIEW_PATH.exec(path);
  return {
    path,
    interview_id: match?.[1] ?? null,
  };
}

/**
 * Sends presence heartbeats while the user is signed in.
 * Failures are swallowed — presence must never break the app shell.
 */
export function usePresenceHeartbeat(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    let cancelled = false;

    const beat = () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      const payload = currentPresencePayload();
      void api.me.presence(payload).catch(() => {
        /* ignore transient network / rate-limit errors */
      });
    };

    beat();
    const timer = window.setInterval(beat, HEARTBEAT_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') beat();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
}
